import React, { useRef, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import TextField from "@material-ui/core/TextField";
import { Grid, Box, Button, InputLabel, Typography } from "@material-ui/core";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import PropTypes from "prop-types";
import _uniqueId from "lodash/uniqueId";
import { countries, getStateList } from "../constants/dropdown";
import URLS from "../constants/URL";
import LargeImageUrl from "../images/desktop.png";
import MobileImageUrl from "../images/mobile.png";
import "./EasyApplyForm.css";
import ComboSelectBox from "./ComboSelectBox";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ResumeUploadDropzone from "./ResumeUploadDropzone";
import { prepareSaveData } from "./PrepareData";
import { mapParsedResumeToForm, isParseResumeError } from "./parseResumeData";
import { requestAPI } from "./serviceUtil";
import CustomizedSnackbar from "./Snackbar";
import Loader from "./Loader";
import useMediaQuery from "@material-ui/core/useMediaQuery";

const sourceOptions = [
  { value: "WEBSITE", title: "Website" },
  { value: "CAREER_BUILDER", title: "Career Builder" },
  { value: "DICE", title: "Dice" },
  { value: "GLASS_DOOR", title: "Glass Door" },
  { value: "INDEED", title: "Indeed" },
  { value: "LINKEDIN", title: "LinkedIn" },
  { value: "MONSTER", title: "Monster" },
  { value: "STACK_OVERFLOW", title: "Stack Overflow" },
  { value: "ZIPRECRUITER", title: "Ziprecruiter" },
  { value: "INTERNAL", title: "Internal" },
  { value: "EMAIL_MARKETING", title: "Email Marketing" },
];

const fieldStyle = { width: "100%" };

const ReqDetailsBlock = ({ qrid, qjt, inline }) => (
  <div className={`easy-apply-req-details ${inline ? "easy-apply-req-details--inline" : ""}`}>
    <p className="easy-apply-req-details__header">Requisition Details</p>
    <p className="easy-apply-req-details__text">
      Requisition Number:{" "}
      <span className="easy-apply-req-details__value">{qrid}</span>
    </p>
    <p className="easy-apply-req-details__text" style={{ marginBottom: 0 }}>
      Position Title:{" "}
      <span className="easy-apply-req-details__value">{qjt}</span>
    </p>
  </div>
);

const styles = {
  textFieldBg: {
    backgroundColor: "#fff",
    width: "100%",
  },
};

const validation = (regex, value) => {
  if (value === "" || regex.test(value.trim())) {
    return true;
  }
  return false;
};
const stringValidation = (value) => {
  return validation(/^[ a-zA-Z]+$/, value);
};
const cityValidation = (value) => {
  return validation(/^[ A-Za-z-,.']+$/, value);
};
const zipValidation = (value) => {
  return validation(/^[ A-Za-z0-9]{1,8}$/g, value);
};
const checkLength = (value) => {
  return !value ? true : (value + "").length >= 5 && (value + "").length <= 8;
};

const RequiredMark = () => (
  <span className="easy-apply-form__required" aria-hidden="true">
    {" "}
    *
  </span>
);

const FieldLabel = ({ htmlFor, children, required = true }) => (
  <InputLabel htmlFor={htmlFor} className="easy-apply-form__label" shrink={false}>
    {children}
    {required ? <RequiredMark /> : null}
  </InputLabel>
);

const EasyApplyForm = (props) => {
  const formikRef = useRef(null);
  const [state, setState] = useState({
    initialValue: {
      fullName: "",
      email: "",
      phoneCountryCode: "1",
      phone: "",
      country: "USA",
      state: "",
      city: "",
      zip: "",
      source: "",
      candidate_resume: [],
      requisitionNumber: "",
      positionTitle: "",
      action: "SUBMIT",
    },
    isShowLoading: false,
    stateName: getStateList("USA"),
    file: "",
    resumeValidation: false,
  });
  const [isAppliedSuccess, setIsAppliedSuccess] = useState(false);
  const [showSnack, setShowSnack] = useState({});
  const showSnackMessage = (type, message) => {
    const id = _uniqueId("prefix-");
    const obj = {
      type,
      message,
      snackOpen: id,
    };
    setShowSnack(obj);
  };

  const validationSchema = Yup.object({
    fullName: Yup.string().required(" "),
    email: Yup.string().email("Enter a valid email").required(" "),
    phone: Yup.string()
      .required(" ")
      .length(10, "Phone number must be 10 digit"),
    country: Yup.string().required(" "),
    state: Yup.string().when("country", {
      is: (val) => val !== "OTHER",
      then: Yup.string().required(" "),
      otherwise: Yup.string(),
    }),
    city: Yup.string().required(" "),
    zip: Yup.string()
      .required(" ")
      .test("len", "Zip must be 5-8 characters", (value) => checkLength(value)),
    source: Yup.string().required(" "),
  });

  const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });
  let qrid = params.rid;
  let qjt = params.jt;


  const saveApplication = async (data) => {
    setState({
      ...state,
      isShowLoading: true,
    });
    data.candidate_resume = state.file;
    data.requisitionNumber = qrid === null || qrid === undefined ? "" : qrid;
    data.positionTitle = qjt === null || qjt === undefined ? "" : qjt;
    const formData = prepareSaveData(data);
    const response = await requestAPI(
      URLS.createCandidate,
      formData,
      "POST",
      "createCandidate"
    );
    if (response.message === "SUCCESS - SUBMIT") {
      setState({
        ...state,
        isShowLoading: false,
      });
      setIsAppliedSuccess(true);
      setTimeout(() => {
        window.open("https://www.ptechpartners.com/thank-you-g/", "_self");
      }, 5000);
    } else {
      setState({
        ...state,
        isShowLoading: false,
      });
      showSnackMessage("error", "Something went wrong. Please try again.");
    }
  };
  const fillFormFromParsedResume = (candidate) => {
    const parsed = mapParsedResumeToForm(candidate);
    if (!parsed || !formikRef.current) {
      return false;
    }

    const { setFieldValue } = formikRef.current;
    const fields = [
      "fullName",
      "email",
      "phoneCountryCode",
      "phone",
      "country",
      "state",
      "city",
      "zip",
      "source",
    ];

    fields.forEach((field) => {
      if (parsed[field]) {
        setFieldValue(field, parsed[field]);
      }
    });

    if (parsed.country) {
      setState((prevState) => ({
        ...prevState,
        stateName: parsed.stateList,
      }));
    }

    return fields.some((field) => Boolean(parsed[field]));
  };

  const uploadResume = async (file) => {
    setState((prev) => ({
      ...prev,
      isShowLoading: true,
      resumeValidation: false,
    }));
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await requestAPI(
        URLS.parseResume,
        formData,
        "POST",
        "uploadFile"
      );
      if (isParseResumeError(response)) {
        showSnackMessage("error", "Something went wrong. Please try again.");
      } else {
        const didAutofill = fillFormFromParsedResume(response);
        showSnackMessage(
          "success",
          didAutofill
            ? "Resume uploaded and form filled automatically!"
            : "File uploaded successfully!"
        );
      }
    } finally {
      setState((prevState) => ({
        ...prevState,
        isShowLoading: false,
      }));
    }
  };
  const handleFileUpload = async ({ target }) => {
    const { name, files } = target;
    if (!files || !files.length) return;

    var fileList = [];
    for (var i = 0; i < files.length; i++) {
      fileList.push(files[i]);
    }
    setState((prev) => ({
      ...prev,
      file: fileList,
      resumeValidation: false,
    }));

    if (name === "candidate_resume") {
      await uploadResume(files[0]);
    }
  };
  function getStateListAll(selectedCountry) {
    const stateList = getStateList(selectedCountry);                                                      
    setState((prevState) => ({
      ...prevState,
      initialValue: {
        ...prevState.initialValue,
        country: selectedCountry,
        state: "",
      },
      stateName: stateList,
    }));
  }
  const isDesktop = useMediaQuery("(min-width:1280px)");

  const renderFormFields = (formikProps) => {
    const { errors, touched, handleChange, values } = formikProps;

    return (
      <div
        className={`easy-apply-form ${
          isDesktop ? "easy-apply-form--desktop" : ""
        }`}
      >
        <div className="easy-apply-form__field">
          <ResumeUploadDropzone
            onChange={handleFileUpload}
            name="candidate_resume"
            id="candidate_resume"
            accept=".pdf,.doc,.docx"
            selectedFile={
              state.file && state.file.length > 0 ? state.file[0] : null
            }
            onRemove={() => {
              setState((prev) => ({
                ...prev,
                file: [],
                resumeValidation: true,
              }));
            }}
            error={state.resumeValidation && !state.file}
            required
          />
        </div>

        <div className="easy-apply-form__field">
          <FieldLabel htmlFor="fullName">Full Name</FieldLabel>
          <TextField
            id="fullName"
            name="fullName"
            variant="outlined"
            placeholder="Enter Your Full Name"
            value={values.fullName}
            onChange={(e) => {
              e.preventDefault();
              if (stringValidation(e.target.value)) handleChange(e);
            }}
            helperText={touched.fullName ? errors.fullName : ""}
            error={touched.fullName && Boolean(errors.fullName)}
            margin="none"
            fullWidth
            style={styles.textFieldBg}
          />
        </div>

        <div className="easy-apply-form__field">
          <FieldLabel htmlFor="email">Email</FieldLabel>
          <TextField
            id="email"
            name="email"
            value={values.email}
            placeholder="eg.petegabiuser123@outlook.com"
            onChange={handleChange}
            variant="outlined"
            helperText={touched.email ? errors.email : ""}
            error={touched.email && Boolean(errors.email)}
            margin="none"
            fullWidth
            style={styles.textFieldBg}
          />
        </div>

        <div className="easy-apply-form__field-row easy-apply-form__field-row--split">
          <div className="easy-apply-form__field">
            <FieldLabel htmlFor="phone">Phone Number</FieldLabel>
            <PhoneInput
              country={"us"}
              value={values.phoneCountryCode + values.phone}
              onChange={(value, countryData) => {
                const dialCode = countryData.dialCode || "";
                const phoneNumber = value.slice(dialCode.length);
                values.phoneCountryCode = dialCode;
                values.phone = phoneNumber;
              }}
              inputProps={{
                name: "phone",
                id: "phone",
              }}
              enableSearch={true}
              searchPlaceholder="Search country..."
            />
            {touched.phone && errors.phone && (
              <p className="easy-apply-form__phone-error">{errors.phone}</p>
            )}
          </div>
          <div className="easy-apply-form__field">
            <FieldLabel htmlFor="country">Country</FieldLabel>
            <ComboSelectBox
              name="country"
              id="country"
              options={countries}
              displayLabel="Your Country"
              onChange={(e) => {
                getStateListAll(e.target.value);
              }}
              style={fieldStyle}
            />
          </div>
        </div>

        <div className="easy-apply-form__field-row easy-apply-form__field-row--split">
          <div className="easy-apply-form__field">
            <FieldLabel htmlFor="state" required={values.country !== "OTHER"}>
              State
            </FieldLabel>
            <ComboSelectBox
              name="state"
              id="state"
              options={state.stateName || []}
              displayLabel="Your State"
              disabled={values.country === "OTHER"}
              style={fieldStyle}
            />
          </div>
          <div className="easy-apply-form__field">
            <FieldLabel htmlFor="city">City</FieldLabel>
            <TextField
              id="city"
              name="city"
              placeholder="Your City"
              value={values.city}
              onChange={(e) => {
                e.preventDefault();
                if (cityValidation(e.target.value)) handleChange(e);
              }}
              variant="outlined"
              helperText={touched.city ? errors.city : ""}
              error={touched.city && Boolean(errors.city)}
              margin="none"
              fullWidth
              style={styles.textFieldBg}
            />
          </div>
        </div>

        <div className="easy-apply-form__field-row easy-apply-form__field-row--split">
          <div className="easy-apply-form__field">
            <FieldLabel htmlFor="zip">Zipcode</FieldLabel>
            <TextField
              id="zip"
              name="zip"
              placeholder="Zipcode"
              value={values.zip}
              maxLength="8"
              onChange={(e) => {
                e.preventDefault();
                if (zipValidation(e.target.value)) handleChange(e);
              }}
              variant="outlined"
              helperText={touched.zip ? errors.zip : ""}
              error={touched.zip && Boolean(errors.zip)}
              margin="none"
              fullWidth
              style={styles.textFieldBg}
            />
          </div>
          <div className="easy-apply-form__field">
            <FieldLabel htmlFor="source">Source</FieldLabel>
            <ComboSelectBox
              name="source"
              id="source"
              options={sourceOptions}
              displayLabel="Source"
              style={fieldStyle}
            />
          </div>
        </div>

        <Typography className="easy-apply-form__legal" variant="body2">
          By submitting, I have read and agree to the{" "}
          <a
            href="https://www.ptechpartners.com/privacy-policy-2/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#1a337e", textDecoration: "underline" }}
          >
            Privacy Policy
          </a>{" "}
          &amp;{" "}
          <a
            href="https://www.ptechpartners.com/terms-and-conditions/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#1a337e", textDecoration: "underline" }}
          >
            Terms &amp; Conditions
          </a>
        </Typography>

        <div className="easy-apply-form__submit-wrap">
          <Button
            variant="contained"
            type="submit"
            className="easy-apply-form__submit"
          >
            Submit
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="easy-apply-page">
    <Grid container>
      <Grid
        item
        xs={12}
        lg={3}
        className={`easy-apply-sidebar ${
          isDesktop ? "easy-apply-sidebar--desktop" : ""
        }`}
        style={{
          position: isDesktop ? "sticky" : "static",
          top: 0,
          alignSelf: "flex-start",
        }}
      >
        <Box
          component="img"
          className="easy-apply-sidebar__image"
          alt="Easy Apply User Interface"
          src={isDesktop ? LargeImageUrl : MobileImageUrl}
        />
      </Grid>
      {!isDesktop && (
        <Grid item xs={12}>
          <ReqDetailsBlock qrid={qrid} qjt={qjt} inline />
        </Grid>
      )}
      <Grid item xs={12} lg={9} className="easy-apply-main">
        {!isAppliedSuccess ? (
          <Formik
            innerRef={formikRef}
            initialValues={state.initialValue}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              if (state.file && state.file.length > 0) {
                saveApplication(values);
              }
            }}
          >
            {({ errors, touched, handleSubmit, handleChange, values, setTouched }) => (
              <form
                id="easy-apply-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  const hasResume = state.file && state.file.length > 0;
                  if (!hasResume) {
                    setState((prev) => ({ ...prev, resumeValidation: true }));
                  }
                  setTouched({
                    fullName: true,
                    email: true,
                    phone: true,
                    country: true,
                    state: true,
                    city: true,
                    zip: true,
                    source: true,
                  });
                  handleSubmit(event);
                }}
              >
                {state.isShowLoading && <Loader />}
                <Grid container>
                  <Grid item xs={12} lg={7} className="easy-apply-form-wrap">
                    {renderFormFields({
                      errors,
                      touched,
                      handleChange,
                      values,
                    })}
                  </Grid>
                  {isDesktop && (
                    <Grid item xs={12} lg={5} className="easy-apply-details-panel">
                      <div className="easy-apply-details-panel__line" />
                      <div className="easy-apply-details-panel__content">
                        <ReqDetailsBlock qrid={qrid} qjt={qjt} />
                      </div>
                    </Grid>
                  )}
                </Grid>
              </form>
            )}
          </Formik>
        ) : (
          <Box className="easy-apply-success" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
            <CheckCircleOutlineIcon className="easy-apply-success__icon" />
            <Typography className="easy-apply-success__title" variant="h4">
              Thank You for Applying!
            </Typography>
            <Typography className="easy-apply-success__body" variant="body1">
              We've received your application and our team is reviewing it. If your
              resume is shortlisted, our AI assistant will reach out for a
              pre-qualification call to verify your details and discuss the role.
            </Typography>
          </Box>
        )}
      </Grid>
      {showSnack.type ? <CustomizedSnackbar alertMsg={showSnack} /> : null}
    </Grid>
    </div>
  );
};

EasyApplyForm.propTypes = {
  resumeValidation: PropTypes.bool,
};

export default EasyApplyForm;
